import { useState, type FormEvent } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { supabase } from '../../../lib/supabase';
import AppDialog from '../../../components/AppDialog';
import { useModalHistory } from '../../../utils/useModalHistory';
import SpinnerButton from '../../../components/SpinnerButton';
import { EDIT_PROFILE_MODAL_LABELS as LABELS } from '../../../utils/labels';

interface EditProfileModalProps {
  session: Session;
  onClose: () => void;
}

export default function EditProfileModal({ session, onClose }: EditProfileModalProps) {
  useModalHistory(onClose);
  const { first_name = '', last_name = '' } = session.user.user_metadata ?? {};
  const [firstName, setFirstName] = useState(first_name);
  const [lastName, setLastName] = useState(last_name);
  const [email, setEmail] = useState(session.user.email ?? '');
  const [errors, setErrors] = useState({ firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const fieldErrors = {
      firstName: !firstName.trim() ? LABELS.firstNameRequired : '',
      lastName: !lastName.trim() ? LABELS.lastNameRequired : '',
      email: !email.trim() ? LABELS.emailRequired : '',
    };
    if (Object.values(fieldErrors).some(Boolean)) {
      setErrors(fieldErrors);
      return;
    }
    setSaving(true);
    try {
      const emailChanged = email.trim() !== (session.user.email ?? '');
      const updates: Parameters<typeof supabase.auth.updateUser>[0] = {
        data: { first_name: firstName.trim(), last_name: lastName.trim() },
      };
      if (emailChanged) updates.email = email.trim();
      await supabase.auth.updateUser(updates);
      if (emailChanged) {
        setSentToEmail(email.trim());
        setEmailSent(true);
      } else {
        window.location.reload();
      }
    } catch {
      // save failed — form stays open
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAfterEmailSent = () => {
    onClose();
    window.location.reload();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={LABELS.title} confirmLabel={emailSent ? undefined : LABELS.save} confirmFormId="edit-profile-form" confirmDisabled={saving} confirmLoading={saving} testId="edit-profile-modal">
      {emailSent ? (
        <>
          <DialogContent>
            <Alert severity="success" className="mt-2">
              {LABELS.emailSentMsg(sentToEmail)}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAfterEmailSent} variant="contained">
              {LABELS.close}
            </Button>
          </DialogActions>
        </>
      ) : (
        <form id="edit-profile-form" onSubmit={handleSubmit} noValidate>
          <DialogContent className="flex flex-col gap-4">
            <div className="flex gap-3 mt-2">
              <TextField
                label={LABELS.firstNameLabel}
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: '' })); }}
                required
                autoFocus
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName || undefined}
              />
              <TextField
                label={LABELS.lastNameLabel}
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: '' })); }}
                required
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName || undefined}
              />
            </div>
            <TextField
              label={LABELS.emailLabel}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((prev) => ({ ...prev, email: '' })); }}
              required
              fullWidth
              error={!!errors.email}
              helperText={errors.email || undefined}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
            <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
              {LABELS.save}
            </SpinnerButton>
          </DialogActions>
        </form>
      )}
    </AppDialog>
  );
}
